import { Button } from "@monbits/ui";
import { CheckCheck, Copy, Download } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

interface TwoFactorBackupCodesProps {
	codes: string[];
	onDownload?: () => void;
	onClose?: () => void;
	showWarning?: boolean;
}

export const TwoFactorBackupCodes: React.FC<TwoFactorBackupCodesProps> = ({
	codes,
	onDownload,
	onClose,
	showWarning = true,
}) => {
	const [copied, setCopied] = React.useState(false);
	// Locked until the user has copied or downloaded at least once. Backup
	// codes are unrecoverable; an accidental Continue here locks the user
	// out if they ever lose their authenticator app.
	const [hasSavedCodes, setHasSavedCodes] = React.useState(false);

	const handleCopy = () => {
		const text = codes.join("\n");
		navigator.clipboard.writeText(text);
		setCopied(true);
		setHasSavedCodes(true);
		toast.success("Backup codes copied");
		setTimeout(() => setCopied(false), 2000);
	};

	const handleDownload = () => {
		const text = codes.join("\n");
		const blob = new Blob([text], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `monbitspay-backup-codes-${Date.now()}.txt`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		setHasSavedCodes(true);
		toast.success("Backup codes saved to your device");
		onDownload?.();
	};

	return (
		<div className="space-y-4">
			{showWarning && (
				<div className="p-3 bg-(--warning-bg) border border-(--warning-border) rounded-lg">
					<p className="text-sm text-(--warning-fg)">
						These codes won't be shown again. Save them somewhere safe — you'll
						need one if you lose your authenticator app.
					</p>
				</div>
			)}

			<div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
				{codes.map((code) => (
					<div key={code} className="py-1">
						{code}
					</div>
				))}
			</div>

			<div className="flex gap-2">
				<Button onClick={handleCopy} variant="outline" className="flex-1">
					{copied ? (
						<>
							<CheckCheck className="mr-2 h-4 w-4" />
							Copied
						</>
					) : (
						<>
							<Copy className="mr-2 h-4 w-4" />
							Copy
						</>
					)}
				</Button>

				<Button onClick={handleDownload} variant="outline" className="flex-1">
					<Download className="mr-2 h-4 w-4" />
					Download
				</Button>
			</div>

			{onClose && (
				<Button onClick={onClose} className="w-full" disabled={!hasSavedCodes}>
					{hasSavedCodes ? "I've saved them" : "Copy or download to continue"}
				</Button>
			)}
		</div>
	);
};
